<template>
  <TypeformQuestion question="Verify Your Phone Number" center
                    :loading="loading"
                    :subtitle="`Code sent to ${formatPhoneNumber(store.newUserDetails.phone)}`"
                    @prev-step="emits('prevStep')" @next-step="emits('nextStep')">

    <FormVerificationCodeInput v-model="store.newUserPassword.smsVerificationCode"
                               :locked="locked"
                               :custom-color-styles="customColorStyles"
                               focus-on-mount :focus-delay="400"/>

    <p class="text-sm select-none text-center my-8">

      <span class="opacity-60">Didn't receive a code? </span>
      <button type="button" @click="requestNewVerificationCode"
         class="underline cursor-pointer opacity-60 hover:opacity-100">
        <span v-if="countdown !== 0" class="w-92">Request again in {{ countdown }}s</span>
        <span v-else>Send new verification code</span>
      </button>

    </p>

    <FormCheckbox id="smsNotifications" custom-colors v-model="store.newUserDetails.enableDefaultSmsNotifications" class="mb-4">
      Send time-sensitive notifications to me via text messages (recommended)
    </FormCheckbox>

  </TypeformQuestion>
</template>

<script setup>
import AccountApi from "../api/AccountApi.js";
import FormCheckbox from "./FormCheckbox.vue";
import FormVerificationCodeInput from "./FormVerificationCodeInput.vue";
import TypeformQuestion from "./TypeformQuestion.vue";
import {useCandidateApplicationFormStore} from "../stores/CandidateApplicationFormStore.js";
import {computed, onMounted, ref} from "vue";
import {formatPhoneNumber} from "../helpers/formatters";

const emits = defineEmits(["prevStep", "nextStep", "update:validator", "update:validating"]);
const store = useCandidateApplicationFormStore();
const loading = ref(false);
const locked = ref(false);
const countdown = ref(0);

const hexToRgbaWithOpacity = (hex, opacity) => {
  let c = hex.substring(1);
  if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];

  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);

  return `rgba(${r},${g},${b},${opacity})`;
};

const customColorStyles = computed(() => `background-color:${hexToRgbaWithOpacity(store.applicationForm.primaryForeground, 0.1)};color:${store.applicationForm.primaryForeground};`);

const requestNewVerificationCode = async () => {
  if (countdown.value !== 0 || loading.value) return;
  loading.value = true;

  const phone = store.newUserDetails.phone;

  try {
    const response = await AccountApi.sendVerificationSms({phone});

    if (response?.error) {
      toast.error(response.error);
    } else {
      toast.success("Verification code sent!");
      store.lastPhoneCodeSentTo = phone;
      restartTimer();
    }
  } catch (e) {
    emits("prevStep");
  } finally {
    loading.value = false;
  }
};

const restartTimer = () => {
  if (countdown.value === 0) countdown.value = 59;
  setTimeout(() => {
    countdown.value -= 1;
    if (countdown.value > 0) {
      restartTimer();
    }
  }, 1000);
};

onMounted(() => {
  if (!store.newUserDetails.phone) {
    emits("prevStep");
    return;
  }

  store.setFurthestStep(3, "Verify Your Phone Number");

  emits("update:validator", async () => {
    if (store.newUserPassword.smsVerificationCode.length !== 6) {
      toast.error("Please enter a valid 6-digit code.");
      return false;
    }

    if (store.newUserDetails.phone === store.verifiedPhoneNumber) return true;

    emits("update:validating", true);
    loading.value = true;

    try {
      const phone = store.newUserDetails.phone;

      const response = await AccountApi.verifySmsCode({
        phone,
        code: store.newUserPassword.smsVerificationCode,
      });

      if (response?.error) {
        toast.error(response.error);
        emits("update:validating", false);
        loading.value = false;
        return false;
      }

      store.verifiedPhoneNumber = phone;
      locked.value = true;

      await store.handleCreateNewUser();

      emits("update:validating", false);
      loading.value = false;
      return true;
    } catch (e) {
      toast.error("An error occurred. Please try again.");
      emits("update:validating", false);
      loading.value = false;
      return false;
    }
  });

  if (store.newUserDetails.phone !== store.lastPhoneCodeSentTo) {
    locked.value = false;
    requestNewVerificationCode();
  }

  if (store.newUserDetails.phone === store.verifiedPhoneNumber) {
    locked.value = true;
  }
});
</script>
